import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useHistory } from "react-router-dom";
import { makeStyles } from "tss-react/mui";

import {
	Box,
	Button,
	CircularProgress,
	Divider,
	IconButton,
	Paper,
	Tooltip,
	Typography
} from "@mui/material";

import FormatListBulletedIcon from "@mui/icons-material/FormatListBulleted";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import RefreshIcon from "@mui/icons-material/Refresh";

import { AuthContext } from "../../context/Auth/AuthContext";
import socket from "../../hooks/useSocket";
import api from "../../services/api";
import ToastError from "../../toast/error/toastError";

const useStyles = makeStyles()((theme) => ({
	root: {
		display: "flex",
		flexDirection: "column",
		gap: theme.spacing(1.5),
		padding: theme.spacing(2),
		width: "100%",
		height: "100%",
		overflow: "auto",
		boxSizing: "border-box",
	},
	card: {
		padding: theme.spacing(2),
		borderRadius: 4,
		border: `1px solid ${theme.palette.divider}`,
		backgroundColor: theme.palette.background.paper,
		boxShadow: "none",
	},
	heroHeader: {
		display: "flex",
		alignItems: "baseline",
		justifyContent: "space-between",
		marginBottom: theme.spacing(1.5),
	},
	heroLabel: {
		fontSize: 11,
		fontWeight: 600,
		textTransform: "uppercase",
		letterSpacing: 0.8,
		color: theme.palette.text.secondary,
	},
	heroCount: {
		fontSize: 36,
		fontWeight: 600,
		lineHeight: 1.1,
		color: theme.palette.text.primary,
		fontVariantNumeric: "tabular-nums",
	},
	heroSub: {
		fontSize: 12,
		color: theme.palette.text.secondary,
		marginTop: theme.spacing(0.25),
	},
	primaryButton: {
		marginTop: theme.spacing(1.5),
		padding: theme.spacing(1, 2),
		fontSize: 13,
		fontWeight: 600,
		borderRadius: 4,
		textTransform: "none",
		width: "100%",
		boxShadow: "none",
		"&:hover": { boxShadow: "none" },
	},
	secondaryButton: {
		marginTop: theme.spacing(0.75),
		fontSize: 11,
		textTransform: "none",
		color: theme.palette.text.secondary,
		fontWeight: 500,
	},
	statsRow: {
		display: "grid",
		gridTemplateColumns: "1fr 1fr",
		gap: theme.spacing(1),
	},
	statCard: {
		padding: theme.spacing(1.5),
		borderRadius: 4,
		border: `1px solid ${theme.palette.divider}`,
		backgroundColor: theme.palette.background.paper,
	},
	statLabel: {
		fontSize: 10,
		fontWeight: 600,
		textTransform: "uppercase",
		letterSpacing: 0.5,
		color: theme.palette.text.secondary,
		marginBottom: theme.spacing(0.5),
	},
	statValue: {
		fontSize: 20,
		fontWeight: 600,
		color: theme.palette.text.primary,
		fontVariantNumeric: "tabular-nums",
	},
	statHint: {
		fontSize: 10,
		color: theme.palette.text.disabled,
		marginTop: theme.spacing(0.25),
	},
	statQueueLabel: {
		fontSize: 10,
		color: theme.palette.text.secondary,
		marginTop: theme.spacing(0.25),
		overflow: "hidden",
		textOverflow: "ellipsis",
		whiteSpace: "nowrap",
	},
	queueHeader: {
		display: "flex",
		alignItems: "center",
		justifyContent: "space-between",
		marginBottom: theme.spacing(1),
	},
	queueRow: {
		display: "grid",
		gridTemplateColumns: "10px 1fr auto auto",
		alignItems: "center",
		gap: theme.spacing(1.5),
		padding: theme.spacing(0.75, 0),
	},
	queueDot: {
		width: 8,
		height: 8,
		borderRadius: "50%",
	},
	queueName: {
		fontSize: 12,
		fontWeight: 500,
		color: theme.palette.text.primary,
		overflow: "hidden",
		textOverflow: "ellipsis",
		whiteSpace: "nowrap",
	},
	queueWait: {
		fontSize: 11,
		color: theme.palette.text.secondary,
		fontVariantNumeric: "tabular-nums",
		minWidth: 60,
		textAlign: "right",
	},
	queueCount: {
		fontSize: 11,
		fontWeight: 600,
		color: theme.palette.text.primary,
		fontVariantNumeric: "tabular-nums",
		minWidth: 24,
		textAlign: "right",
	},
	emptyState: {
		textAlign: "center",
		padding: theme.spacing(3, 2),
		color: theme.palette.text.secondary,
		fontSize: 13,
	},
}));

const formatDuration = (seconds) => {
	if (!seconds || seconds < 0) return "0s";
	const s = Math.floor(seconds);
	const h = Math.floor(s / 3600);
	const m = Math.floor((s % 3600) / 60);
	const sec = s % 60;
	if (h > 0) return `${h}h ${String(m).padStart(2, "0")}m`;
	if (m > 0) return `${m}m ${String(sec).padStart(2, "0")}s`;
	return `${sec}s`;
};

const severityColor = (theme, seconds) => {
	if (seconds > 3600) return theme.palette.error.main;
	if (seconds > 1800) return theme.palette.warning.main;
	return theme.palette.text.primary;
};

const LiveDuration = React.memo(function LiveDuration({ startedAt, severity = false }) {
	const { theme } = useStyles();
	const start = useMemo(() => {
		if (!startedAt) return 0;
		const d = startedAt instanceof Date ? startedAt : new Date(startedAt);
		return isNaN(d.getTime()) ? 0 : d.getTime();
	}, [startedAt]);

	const [seconds, setSeconds] = useState(() =>
		start ? Math.max(0, Math.floor((Date.now() - start) / 1000)) : 0
	);

	useEffect(() => {
		if (!start) return;
		setSeconds(Math.max(0, Math.floor((Date.now() - start) / 1000)));
		const id = setInterval(() => {
			setSeconds(Math.max(0, Math.floor((Date.now() - start) / 1000)));
		}, 1000);
		return () => clearInterval(id);
	}, [start]);

	if (!start) return <span>—</span>;

	const style = severity ? { color: severityColor(theme, seconds) } : undefined;
	return <span style={style}>{formatDuration(seconds)}</span>;
});

const PendingTicketsNextMode = ({
	companyId,
	showAll,
	selectedQueueIds = [],
	reloadCount,
	canSwitchToList = false,
	onSwitchToList,
}) => {
	const { classes, theme } = useStyles();
	const { user } = useContext(AuthContext);
	const history = useHistory();

	const [stats, setStats] = useState({ count: 0, avgWaitSeconds: 0, oldestTicket: null, byQueue: [] });
	const [loading, setLoading] = useState(true);
	const [accepting, setAccepting] = useState(false);

	const reloadCountRef = useRef(reloadCount);
	useEffect(() => { reloadCountRef.current = reloadCount; }, [reloadCount]);
	const showAllRef = useRef(showAll);
	useEffect(() => { showAllRef.current = showAll; }, [showAll]);

	const queueIdsParam = useMemo(() => JSON.stringify(selectedQueueIds), [selectedQueueIds]);
	const queueIdsRef = useRef(queueIdsParam);
	useEffect(() => { queueIdsRef.current = queueIdsParam; }, [queueIdsParam]);

	const isMounted = useRef(true);
	useEffect(() => () => { isMounted.current = false; }, []);

	const fetchStats = useCallback(async () => {
		try {
			const { data } = await api.get("/tickets/pending-stats", {
				params: { showAll: showAllRef.current, queueIds: queueIdsRef.current }
			});
			if (!isMounted.current) return;
			setStats(data);
			reloadCountRef.current?.(data.count);
		} catch (err) {
			if (!isMounted.current) return;
			ToastError(err);
		} finally {
			if (isMounted.current) setLoading(false);
		}
	}, []);

	useEffect(() => {
		fetchStats();
	}, [fetchStats, showAll, queueIdsParam]);

	useEffect(() => {
		const userCompany = user?.companyId || companyId;
		if (!userCompany) return;

		let debounce = null;
		const scheduleRefetch = () => {
			if (debounce) clearTimeout(debounce);
			debounce = setTimeout(() => {
				if (isMounted.current) fetchStats();
			}, 500);
		};

		const onTicket = () => scheduleRefetch();
		const onAppMessage = (data) => {
			if (data?.action === "create" && data?.ticket?.status === "pending") scheduleRefetch();
		};

		socket.on(`ticket-${userCompany}`, onTicket);
		socket.on(`appMessage-${userCompany}`, onAppMessage);
		const poll = setInterval(() => { if (isMounted.current) fetchStats(); }, 30000);

		return () => {
			//socket.off(`ticket-${userCompany}`, onTicket);
			//socket.off(`appMessage-${userCompany}`, onAppMessage);
			clearInterval(poll);
			if (debounce) clearTimeout(debounce);
		};
	}, [user?.companyId, companyId, fetchStats]);

	const handleAcceptNext = useCallback(async () => {
		if (accepting) return;
		setAccepting(true);
		try {
			localStorage.removeItem("ticket_accept");
			const { data } = await api.post("/tickets/accept-next");
			const ticket = data?.ticket;
			if (ticket?.id) {
				localStorage.setItem("ticket_accept", ticket.id);
				document.querySelector("#openTickets")?.click();
				if (ticket.uuid) history.push(`/tickets/${ticket.uuid}`);
			}
			fetchStats();
		} catch (err) {
			ToastError(err);
		} finally {
			if (isMounted.current) setAccepting(false);
		}
	}, [accepting, history, fetchStats]);

	if (loading) {
		return (
			<Box className={classes.root} sx={{ alignItems: "center", justifyContent: "center", display: "flex" }}>
				<CircularProgress size={24} thickness={4} />
			</Box>
		);
	}

	const hasPending = stats.count > 0;
	const avgColor = severityColor(theme, stats.avgWaitSeconds);

	return (
		<Box className={classes.root}>
			<Paper className={classes.card} elevation={0}>
				<Box className={classes.heroHeader}>
					<Box>
						<Typography className={classes.heroLabel}>Aguardando atendimento</Typography>
						<Typography className={classes.heroCount}>{stats.count}</Typography>
						<Typography className={classes.heroSub}>
							{hasPending ? "tickets na fila" : "Nenhum ticket aguardando"}
						</Typography>
					</Box>
				</Box>

				<Button
					className={classes.primaryButton}
					variant="contained"
					color="primary"
					disableElevation
					disabled={!hasPending || accepting}
					onClick={handleAcceptNext}
					startIcon={accepting
						? <CircularProgress size={14} thickness={5} sx={{ color: "inherit" }} />
						: <PlayArrowIcon sx={{ fontSize: 16 }} />
					}
				>
					{accepting ? "Aceitando" : "Atender próximo"}
				</Button>

				{canSwitchToList && onSwitchToList && (
					<Button
						className={classes.secondaryButton}
						size="small"
						variant="text"
						startIcon={<FormatListBulletedIcon sx={{ fontSize: 13 }} />}
						onClick={onSwitchToList}
					>
						Visualizar como lista
					</Button>
				)}
			</Paper>

			<Box className={classes.statsRow}>
				<Box className={classes.statCard}>
					<Typography className={classes.statLabel}>Mais antigo</Typography>
					<Typography className={classes.statValue}>
						<LiveDuration startedAt={stats.oldestTicket?.createdAt} severity />
					</Typography>
					{stats.oldestTicket?.queueName ? (
						<Typography className={classes.statQueueLabel}>
							{stats.oldestTicket.queueName}
						</Typography>
					) : (
						<Typography className={classes.statHint}>—</Typography>
					)}
				</Box>

				<Box className={classes.statCard}>
					<Typography className={classes.statLabel}>Tempo médio</Typography>
					<Typography className={classes.statValue} sx={{ color: avgColor }}>
						{formatDuration(stats.avgWaitSeconds)}
					</Typography>
					<Typography className={classes.statHint}>
						sobre {stats.count} ticket{stats.count !== 1 ? "s" : ""}
					</Typography>
				</Box>
			</Box>

			{stats.byQueue.length > 0 && (
				<Box className={classes.card}>
					<Box className={classes.queueHeader}>
						<Typography className={classes.statLabel}>Distribuição por setor</Typography>
						<Tooltip title="Atualizar">
							<IconButton size="small" onClick={fetchStats} sx={{ p: 0.5 }}>
								<RefreshIcon sx={{ fontSize: 14 }} />
							</IconButton>
						</Tooltip>
					</Box>
					{stats.byQueue.map((q, idx) => (
						<React.Fragment key={q.queueId ?? "no-queue"}>
							{idx > 0 && <Divider />}
							<Box className={classes.queueRow}>
								<Box
									className={classes.queueDot}
									sx={{ backgroundColor: q.queueColor || theme.palette.text.disabled }}
								/>
								<Typography className={classes.queueName}>
									{q.queueName || "Sem setor"}
								</Typography>
								<Typography className={classes.queueWait}>
									<LiveDuration startedAt={q.oldestCreatedAt} />
								</Typography>
								<Typography className={classes.queueCount}>{q.count}</Typography>
							</Box>
						</React.Fragment>
					))}
				</Box>
			)}

			{!hasPending && (
				<Box className={classes.emptyState}>
					Nenhum atendimento aguardando no momento.
				</Box>
			)}
		</Box>
	);
};

export default PendingTicketsNextMode;
